import * as React from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form, useFormikContext } from 'formik';
import {
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { makeStyles } from '@mui/styles';

import { getSubmissionDetails } from 'application/redux/selectors';
import { Spacing, Book, Page } from 'components/Layout';
import { LabeledData, TextInput } from 'components/Input';

import LrButton from 'components/LrButton';
import { UwAdjustmentReviewPage } from 'components/Underwriting';
import { currencyMask, currencyMaskWithNegatives } from 'helpers/masks';
import { currencyToNumber, formatDecimal, formatDollar, formatPercent } from 'helpers/formatters';
import { sendAdjustmentReview } from 'services/endorsements';
import { getTopLevelUserGroup } from 'application/redux/selectors';
import SettingsIcon from '@mui/icons-material/Settings';
import EndorsmentNote from 'components/SubmissionDetails/EndorsementNote';
import DeclineReviewDialog from '../DeclineReviewDialog';
import { adjustmentReviewSchema } from '../ValidationSchema';
import { getIsUnderwriterRole } from 'helpers/roles';
import { AdjustmentGLReviewData, AdjustmentReviewGLLocationData, UwGLLocationReview } from 'types/underwriting.type';
import { fetchAdjustmentGLReviewData } from 'services/uwReview';
import { useEffect } from 'react';
import {
    calculateAdditionalBrokerPremium,
    calculateAnnualizedPremiumFromTotalPremium,
    calculateEndorsementPremium,
} from 'helpers/calculations';
import { calculateTotalGLPremium } from 'helpers/glCalculationHelper';
import { TaxAndFeesInfo } from 'components/TaxAndFees';
import { CircularLoader } from 'components/Loader';
import { TaxData } from 'components/TaxAndFees/components/TaxAndFeesInfoDialog';
import LrDialog from '../../../../../../components/LrDialog';
import UWDetailsAdjustmentGLDialog from './UWDetailsAdjustmentGLDialog';
import { PRODUCT_LINE_TYPES, ROUTES, STATUS_TYPES } from 'constants/index';
import { getRaaSJSON } from '../../../../../../services/generalLiabilty';
import GLPropertyTableRowModel from '../../../../../../components/Underwriting/components/GLPropertyTable/GLPropertyTableRowModel';
import { GLRatingRequest } from '../../../../../../types/generalLiability.type';
import { AdjustmentReviewRequest } from '../../../../../../types/endorsements.type';

interface Props {
    history: any;
}

interface FormData {
    narrative: string;
    premium: string | number;
    policyFee?: string;
    professionalFee?: string;
    slaFee?: string | number;
    slaTax?: string | number;
}

type UpdateTaxesFunc = (
    taxDataParam: TaxData,
    totalFees: number,
    totalPrem: number,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => void;

const AutoUpdateForm = (formProps: {
    totalPrem: number;
    taxData: TaxData;
    isAutoCalc: boolean;
    updateTaxes: UpdateTaxesFunc;
    isBrokerCommissionRated: boolean;
    brokerCommissionPercentage: number;
}) => {
    const { values, setFieldValue } = useFormikContext<FormData>();
    React.useEffect(() => {
        if (formProps.isAutoCalc) {
            const totalFees =
                (values.policyFee ? Number(currencyToNumber(values.policyFee)) : 0) +
                (values.professionalFee ? Number(currencyToNumber(values.professionalFee)) : 0);
            const totalPrem = calculateAdditionalBrokerPremium(
                formProps.isBrokerCommissionRated,
                formProps.totalPrem,
                formProps.brokerCommissionPercentage
            );
            formProps.updateTaxes(formProps.taxData, totalFees, totalPrem, setFieldValue);
        }
        setFieldValue('premium', formProps.totalPrem);
    }, [values.policyFee, values.professionalFee, formProps.totalPrem]);

    return null;
};

const AdjustmentReview: React.FunctionComponent<Props> = (props) => {
    const { history } = props;

    // Global State
    const submission = useSelector(getSubmissionDetails);
    const group = useSelector(getTopLevelUserGroup);

    // Local State
    const isUnderwriter = getIsUnderwriterRole(group);
    const [showDeclineDialog, setShowDeclineDialog] = React.useState(false);
    const [isAutoCalc, setIsAutoCalc] = React.useState(false);
    const [adjustmentReviewData, setAdjustmentReviewData] = React.useState<AdjustmentGLReviewData>(
        {} as AdjustmentGLReviewData
    );
    const [totalPremium, setTotalPremium] = React.useState<number>(0);
    const [action, setAction] = React.useState({ error: false, success: false, inProgress: false });
    const [taxData, setTaxData] = React.useState<TaxData>({});
    const isReadOnly = submission.statusType.code !== STATUS_TYPES.ENDORSEMENT_REQUEST_REVIEW || !isUnderwriter;
    const [taxesLoading, setTaxesLoading] = React.useState(!submission?.producer?.isFileTaxes);
    const [initialValues, setInitialValues] = React.useState<FormData>({
        narrative: '',
        premium: 176,
        policyFee: undefined,
        professionalFee: undefined,
        slaFee: undefined,
        slaTax: undefined,
    });
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [detailsOpen, setDetailsOpen] = React.useState(false);
    const [submittedValues, setSubmittedValues] = React.useState<{ slaFee?: number; slaTax?: number } | undefined>();
    const [applyChangeRetroactive, setApplyChangeRetroactive] = React.useState<boolean>(false);

    const buildRaasPayload = (dataRow: AdjustmentReviewGLLocationData): GLRatingRequest => ({
        genAggLimit: dataRow.propertyGLReview?.genAggLimit,
        premOpsOccurrenceLimit: dataRow.propertyGLReview?.premOpsOccurrenceLimit,
        ratingRequestLocation: {
            locationId: dataRow.locationId,
            premOpsDeductible: dataRow.propertyGLReview?.premOpsDeductible,
        },
    });

    const fetchData = async () => {
        if (submission?.id) {
            const data = await fetchAdjustmentGLReviewData(submission.id);
            if (data) {
                // initialize the "origIrpm"
                data.locationData.forEach((ld) => {
                    ld.origIrpm = ld.irpm;
                    if (!isReadOnly) {
                        ld.grossPrem = -1;
                        ld.baseRatedPremium = -1;
                        ld.proRataPrem = -1;
                    } else if (ld.propertyGLReview) {
                        ld.irpm = ld.propertyGLReview.irpm || ld.irpm;
                        ld.baseRatedPremium = ld.propertyGLReview.baseRatedPremium || ld.baseRatedPremium;
                        ld.grossPrem = ld.propertyGLReview.totalPremium || ld.grossPrem;
                        ld.annualizedGrossPrem = calculateAnnualizedPremiumFromTotalPremium(
                            ld.grossPrem,
                            data.origEffectiveDate,
                            data.origExpirationDate
                        );
                    }
                });
                setApplyChangeRetroactive(data.isRateRetroactive);
                setAdjustmentReviewData(data);
                if (!isReadOnly) {
                    const responses: Promise<any>[] = [];
                    for (const item of data.locationData) {
                        responses.push(
                            getRaaSJSON(buildRaasPayload(item)).then((resp) => {
                                item.baseRatedPremium = Number(resp.baseRatedPremium);
                                if (item.propertyGLReview) {
                                    item.propertyGLReview.baseRatedPremium = resp.baseRatedPremium;
                                    item.propertyGLReview.ratedExposures = resp.glRatedExposures;
                                }
                                return new Promise<void>((r) => r()); // do nothing, this is just so I can "await" below
                            })
                        );
                    }
                    await Promise.all(responses);
                    setAdjustmentReviewData({ ...data });
                }
            }
        }
    };

    const updateDataPremium = (data: AdjustmentGLReviewData) => {
        let totalPrem = 0;
        data.locationData.forEach((d: AdjustmentReviewGLLocationData) => {
            // additional premium is (delta baseRated * new Irpm) + (old baseRated * delta Irpm)
            if (d.baseRatedPremium > 0) {
                const newExposurePremium = calculateTotalGLPremium(
                    d.irpm,
                    d.baseRatedPremium - d.origBaseRatedPremPaid,
                    d.triaPercentage
                );
                const newIrpmOldExposurePremium = calculateTotalGLPremium(
                    d.irpm - d.origIrpm,
                    d.origBaseRatedPremPaid,
                    d.triaPercentage
                );
                d.grossPrem = newExposurePremium + (applyChangeRetroactive ? newIrpmOldExposurePremium : 0);
                d.annualizedGrossPrem = calculateAnnualizedPremiumFromTotalPremium(
                    d.grossPrem,
                    data.origEffectiveDate,
                    data.origExpirationDate
                );
                d.proRataPrem = calculateEndorsementPremium(
                    d.grossPrem,
                    data.origEffectiveDate,
                    data.origExpirationDate,
                    data.newEffectiveDate,
                    data.newExpirationDate
                );
                if (d.propertyGLReview) {
                    d.propertyGLReview.baseRatedPremium = d.baseRatedPremium;
                    d.propertyGLReview.endorsementPremium = d.proRataPrem;
                    d.propertyGLReview.totalPremium = d.grossPrem;
                }
                totalPrem += d.proRataPrem;
            }
        });
        data.totalPrem = totalPrem;
    };

    useEffect(() => {
        if (adjustmentReviewData.locationData) {
            updateDataPremium(adjustmentReviewData);
            setTotalPremium(adjustmentReviewData.totalPrem);
        }
    }, [adjustmentReviewData, applyChangeRetroactive]);

    useEffect(() => {
        fetchData();
    }, [submission]);

    const doSubmit = async (values: any) => {
        try {
            const premiums = {};
            const propertyReviews = {};
            adjustmentReviewData.locationData.forEach((ld) => {
                premiums[ld.locationId] = ld.proRataPrem;
                propertyReviews[ld.locationId] = {
                    ...ld.propertyGLReview,
                    productLineTypeCode: PRODUCT_LINE_TYPES.GENERAL_LIABILITY,
                };
            });
            const payload: AdjustmentReviewRequest = {
                underwriterReview: {
                    premium: Number(currencyToNumber(values.premium)),
                    policyFee: Number(currencyToNumber(values.policyFee)),
                    professionalFee: Number(currencyToNumber(values.professionalFee)),
                    slaFee: Number(currencyToNumber(values.slaFee)),
                    slaTax: Number(currencyToNumber(values.slaTax)),
                },
                premiums: premiums,
                narrative: values.narrative,
                propertyReviews: propertyReviews,
                isRateRetroactive: applyChangeRetroactive,
            };
            await sendAdjustmentReview(submission.id, payload);
            history.push(ROUTES.submissions());
        } catch (e) {
            setAction({
                ...action,
                error: true,
                success: false,
                inProgress: false,
            });
        }
    };

    const submit = (values: any) => {
        setSubmittedValues(values);
        if (!values.slaTax && !values.slaFee) {
            setConfirmOpen(true);
        } else {
            doSubmit(values);
        }
    };

    const confirmSubmit = () => {
        setConfirmOpen(false);
        doSubmit(submittedValues);
    };

    const useStyles = makeStyles({
        table: {
            minWidth: 650,
        },
        retroLabel: {
            marginBottom: '5px',
            '& .MuiFormControlLabel-label': {
                fontSize: '15px',
            },
        },
        retroCheck: {
            margin: '7px',
            border: 'unset',
            '& .MuiSvgIcon-root': { fontSize: 20 },
        },
    });
    const classes = useStyles();
    const typoVariant = 'subtitle2';

    const handleRetroactiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setApplyChangeRetroactive(event.target.checked);
    };

    const renderAdjustmentTable = () => (
        <Book>
            <Page>
                <Table className={classes.table} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Location Name</TableCell>
                            <TableCell>Effective Date</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell align="right">Original Units</TableCell>
                            <TableCell align="right">Updated Units</TableCell>
                            <TableCell align="right">Original Pools</TableCell>
                            <TableCell align="right">Updated Pools</TableCell>
                            <TableCell align="right">IRPM</TableCell>
                            <TableCell align="right">Add'l Annual Prem</TableCell>
                            <TableCell align="right">ProRata Prem.</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adjustmentReviewData?.locationData &&
                            adjustmentReviewData?.locationData.map((row) => (
                                <TableRow key={row.locationName}>
                                    <TableCell component="th" scope="row">
                                        <Typography variant={typoVariant}>{row.locationName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={typoVariant}>
                                            {adjustmentReviewData.newEffectiveDate}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={typoVariant}>{submission.expirationDate}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDecimal(row.origUnits, 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDecimal(row.units, 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDecimal(row.origPools, 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDecimal(row.pools, 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant={typoVariant}>{formatDecimal(row.irpm, 2)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.grossPrem === -1 ? (
                                            <CircularProgress />
                                        ) : (
                                            <Typography variant={typoVariant}>
                                                {formatDecimal(row.annualizedGrossPrem, 0)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.proRataPrem === -1 ? (
                                            <CircularProgress />
                                        ) : (
                                            <Typography variant={typoVariant}>
                                                {formatDollar(row.proRataPrem)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        <TableRow key={'total'}>
                            <TableCell colSpan={3}>
                                <FormControlLabel
                                    className={classes.retroLabel}
                                    control={
                                        <Checkbox
                                            className={classes.retroCheck}
                                            checked={applyChangeRetroactive}
                                            onChange={handleRetroactiveChange}
                                            disabled={isReadOnly}
                                            name="retroactiveExposure"
                                        />
                                    }
                                    label="Apply IRPM Change To Previous Exposure"
                                />
                            </TableCell>
                            <TableCell colSpan={5} />
                            <TableCell>
                                <Typography>Total Premium</Typography>
                            </TableCell>
                            <TableCell align={'right'}>
                                <Typography variant={typoVariant}>{formatDollar(totalPremium)}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Page>
        </Book>
    );

    const updateTaxes = (
        taxDataParam: TaxData,
        totalFees: number,
        totalPrem: number,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
    ) => {
        const totalTaxable = totalPrem + totalFees;
        const factor = Math.pow(10, taxDataParam.roundingPrecisionDigits || 2);
        const totalFeesRate = taxDataParam?.totalFeesRate || 0;
        const totalTaxRate = taxDataParam?.totalTaxRate || 0;
        const slaFee = totalFeesRate >= 1 ? totalFeesRate : Math.ceil(totalFeesRate * totalTaxable * factor) / factor;
        const slaTax = Math.ceil(totalTaxRate * totalTaxable * factor) / factor;
        setFieldValue('slaFee', slaFee);
        setFieldValue('slaTax', slaTax);
    };

    const handleUpdateLocationData = (locationData: UwGLLocationReview[]) => {
        // update the data
        locationData.forEach((ld) => {
            const foundRow = adjustmentReviewData.locationData.find((d) => d.propertyGLReview?.rowId === ld.rowId);
            if (foundRow) {
                foundRow.propertyGLReview = ld;
                foundRow.irpm = ld.irpm || foundRow.irpm;
                foundRow.triaPercentage = ld.triaPercentage || foundRow.triaPercentage;
            }
        });
        setAdjustmentReviewData({ ...adjustmentReviewData });
        setDetailsOpen(false);
    };

    const renderAdjustmentForm = () => (
        <Formik
            initialValues={initialValues}
            isInitialValid={false}
            onSubmit={submit}
            validationSchema={adjustmentReviewSchema}
            enableReinitialize
        >
            {({ errors, handleBlur, handleChange, isSubmitting, isValid, touched, values, setFieldValue }) => (
                <Form>
                    <AutoUpdateForm
                        updateTaxes={updateTaxes}
                        isAutoCalc={isAutoCalc}
                        taxData={taxData}
                        totalPrem={totalPremium}
                        isBrokerCommissionRated={adjustmentReviewData?.isBrokerCommissionRated}
                        brokerCommissionPercentage={adjustmentReviewData?.brokerCommissionPercentage}
                    />
                    <Page title="Other Change Review">
                        <Grid container spacing={3}>
                            <Grid md={3} xs={12}>
                                <LabeledData
                                    title="Premium"
                                    value={values.premium ? formatDollar(values.premium) : '--'}
                                />
                            </Grid>
                            <Grid md={3} xs={12}>
                                <LabeledData
                                    title="Broker Commission"
                                    value={
                                        adjustmentReviewData.brokerCommissionPercentage
                                            ? formatPercent(adjustmentReviewData.brokerCommissionPercentage)
                                            : '--'
                                    }
                                />
                            </Grid>
                            <Grid md={4} xs={9}>
                                <LabeledData title="Home State" value={adjustmentReviewData.homeState} />
                            </Grid>
                            <Grid md={2} xs={3} style={{ display: 'flex' }}>
                                <TaxAndFeesInfo
                                    stateCode={adjustmentReviewData.homeState}
                                    onTaxesAndFeesUpdate={(taxDataParam) => {
                                        if (taxesLoading) {
                                            setTaxesLoading(false);
                                            if (taxDataParam.totalTaxRate) {
                                                setIsAutoCalc(true);
                                                setTaxData(taxDataParam);
                                                updateTaxes(
                                                    taxDataParam,
                                                    0,
                                                    calculateAdditionalBrokerPremium(
                                                        adjustmentReviewData?.isBrokerCommissionRated,
                                                        totalPremium,
                                                        adjustmentReviewData?.brokerCommissionPercentage
                                                    ),
                                                    setFieldValue
                                                );
                                            }
                                        }
                                    }}
                                />
                                {
                                    /*remove button if we don't have locationData*/ adjustmentReviewData.locationData &&
                                        adjustmentReviewData.locationData?.filter((ld) => ld.propertyGLReview)?.length >
                                            0 && (
                                            <IconButton onClick={() => setDetailsOpen(true)} size="large">
                                                <SettingsIcon />
                                            </IconButton>
                                        )
                                }
                            </Grid>
                            {taxesLoading ? (
                                <CircularLoader fillHeight={'25vh'} />
                            ) : (
                                <>
                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title={`State Tax${isAutoCalc ? '(auto)' : ''}`}
                                            name="slaTax"
                                            placeholder="0.00"
                                            value={values.slaTax}
                                            onChange={(event) => {
                                                setIsAutoCalc(false);
                                                handleChange(event);
                                            }}
                                            onBlur={handleBlur}
                                            mask={currencyMask}
                                            errorMessage={touched.slaTax && errors.slaTax}
                                        />
                                    </Grid>
                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title={`Stamping Fee${isAutoCalc ? '(auto)' : ''}`}
                                            name="slaFee"
                                            placeholder="0.00"
                                            value={values.slaFee}
                                            onChange={(event) => {
                                                setIsAutoCalc(false);
                                                handleChange(event);
                                            }}
                                            onBlur={handleBlur}
                                            mask={currencyMask}
                                            errorMessage={touched.slaFee && errors.slaFee}
                                        />
                                    </Grid>

                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title="Policy Fee"
                                            name="policyFee"
                                            placeholder="0.00"
                                            value={values.policyFee}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            mask={currencyMaskWithNegatives}
                                        />
                                    </Grid>
                                    <Grid md={6} xs={12}>
                                        <TextInput
                                            title="Professional Fee"
                                            name="professionalFee"
                                            placeholder="0.00"
                                            value={values.professionalFee}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            mask={currencyMaskWithNegatives}
                                        />
                                    </Grid>
                                    <Grid xs={12}>
                                        <TextInput
                                            value={values.narrative}
                                            name="narrative"
                                            multiline
                                            rows={5}
                                            title="Narrative"
                                            placeholder="Add a message about the change that will be included in the endorsement document"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            errorMessage={touched.narrative && errors.narrative}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Page>
                    <Spacing height="32px" />
                    <Grid container spacing={0} alignItems="center">
                        <LrButton lrType="text" onClick={() => setShowDeclineDialog(true)}>
                            Decline
                        </LrButton>
                        <LrButton lrType="primary" type="submit" disabled={!isValid} isLoading={isSubmitting}>
                            Submit
                        </LrButton>
                    </Grid>
                </Form>
            )}
        </Formik>
    );

    return (
        <>
            <Book>
                <EndorsmentNote submissionId={submission.endorsingSubmissionId} />
            </Book>
            {renderAdjustmentTable()}
            <Book
                leftPage={isReadOnly ? <UwAdjustmentReviewPage areTaxesComplete /> : renderAdjustmentForm()}
                rightPage={
                    <Page title="Other Change Request">
                        <LabeledData title="Effective Date" value={submission.effectiveDate} />
                        <LabeledData title="Notes" value={submission.narrative} />
                    </Page>
                }
            />
            {showDeclineDialog && (
                <DeclineReviewDialog
                    action={action}
                    setAction={setAction}
                    submissionId={submission.id}
                    onCancel={() => setShowDeclineDialog(false)}
                    onSubmit={() => history.push(ROUTES.submissions())}
                />
            )}
            {detailsOpen && (
                <UWDetailsAdjustmentGLDialog
                    onClose={() => setDetailsOpen(false)}
                    onSave={handleUpdateLocationData}
                    isModiedAdjustmentEndorsement={true}
                    isOpen={detailsOpen}
                    data={
                        adjustmentReviewData.locationData.map((d) => ({
                            ...d.propertyGLReview,
                        })) as UwGLLocationReview[]
                    }
                    origEffectiveDate={adjustmentReviewData.origEffectiveDate}
                    origExpDate={adjustmentReviewData.origExpirationDate}
                    newEffectiveDate={adjustmentReviewData.newEffectiveDate}
                    newExpDate={adjustmentReviewData.newExpirationDate}
                />
            )}
            <LrDialog
                title={'Taxes and Fees Missing'}
                isOpen={confirmOpen}
                onSubmit={confirmSubmit}
                onCancel={() => setConfirmOpen(false)}
                submitButtonText={'OK'}
            >
                <Typography>Taxes and Fees are both empty, continue anyway?</Typography>
            </LrDialog>
        </>
    );
};

export default AdjustmentReview;
